/**
 * Homepage Types
 *
 * 3-dimensional model for homepage variant selection:
 * - Gender: male | female
 * - Goal: pregnancy | preserve | egg-donation | sperm-donation
 * - Stage: explore | before-treatment | in-care | time-to-wait | complete
 *          + donor stages: screening | matched | stimulation | retrieval | approved | donating
 *
 * Note: "get-ready" is kept in the HomepageStage type for backward compat
 * but is no longer an active stage — it redirects to explore.
 */

/**
 * Dimension 1: Gender (AMAB/AFAB mapped to male/female)
 */
export type HomepageGender = "male" | "female";

/**
 * Dimension 2: Goal (user's fertility objective)
 */
export type HomepageGoal =
  | "pregnancy"
  | "preserve"
  | "embryo-preserve"
  | "adoption"
  | "surrogacy"
  | "egg-donation"
  | "sperm-donation";

/**
 * Dimension 3: Stage (position in fertility journey)
 *
 * Core stages: explore → before-treatment → in-care → time-to-wait → complete
 * Egg donation stages: explore → screening → matched → stimulation → retrieval → complete
 * Sperm donation stages: explore → screening → approved → donating → complete
 */
export type HomepageStage =
  | "explore" // Exploring options
  | "trying-naturally" // Trying to conceive without treatment
  | "get-ready" // Getting ready (deprecated → explore)
  | "before-treatment" // Before treatment
  | "in-care" // In care
  | "time-to-wait" // Time to wait
  | "complete" // Journey complete
  // Donor-specific stages
  | "screening" // Donor screening phase
  | "matched" // Egg donor matched with recipient
  | "stimulation" // Egg donor ovarian stimulation
  | "retrieval" // Egg donor retrieval day
  | "approved" // Sperm donor approved
  | "donating"; // Sperm donor active donations

/**
 * Full 3D homepage state
 */
export type HomepageState3D = {
  gender: HomepageGender;
  goal: HomepageGoal;
  stage: HomepageStage;
};

/**
 * Serialized state for variant routing (e.g., "female-pregnancy-explore")
 */
export type HomepageStateKey =
  `${HomepageGender}-${HomepageGoal}-${HomepageStage}`;

/**
 * Stage display labels
 */
export const STAGE_LABELS: Record<HomepageStage, string> = {
  explore: "Exploring",
  "trying-naturally": "Trying naturally",
  "get-ready": "Exploring", // Deprecated: get-ready merged into explore
  "before-treatment": "Getting Ready",
  "in-care": "In Care",
  "time-to-wait": "Time to Wait",
  complete: "Journey Complete",
  // Donor stages
  screening: "Screening",
  matched: "Matched",
  stimulation: "Stimulation",
  retrieval: "Retrieval",
  approved: "Approved",
  donating: "Donating",
};

/**
 * Stage IDs (for JourneySteps component)
 */
export const STAGE_IDS: Record<HomepageStage, string> = {
  explore: "exploring",
  "trying-naturally": "trying-naturally",
  "get-ready": "exploring", // Deprecated: get-ready merged into explore
  "before-treatment": "before-treatment",
  "in-care": "in-care",
  "time-to-wait": "time-to-wait",
  complete: "journey-complete",
  // Donor stages
  screening: "screening",
  matched: "matched",
  stimulation: "stimulation",
  retrieval: "retrieval",
  approved: "approved",
  donating: "donating",
};

/**
 * All stages in order (generic)
 */
export const STAGES_IN_ORDER: HomepageStage[] = [
  "explore",
  "trying-naturally",
  "before-treatment",
  "in-care",
  "time-to-wait",
  "complete",
];

/**
 * Stages by goal (simple version - use getStagesForJourney for gender-aware logic)
 *
 * Pregnancy: explore → before-treatment → in-care → time-to-wait → complete
 * Preserve: explore → before-treatment → in-care → complete (female)
 *           explore → before-treatment → in-care → time-to-wait → complete (male)
 */
export const STAGES_BY_GOAL: Record<HomepageGoal, HomepageStage[]> = {
  pregnancy: [
    "explore",
    "trying-naturally",
    "before-treatment",
    "in-care",
    "time-to-wait",
    "complete",
  ],
  preserve: ["explore", "before-treatment", "in-care", "complete"],
  "embryo-preserve": ["explore", "before-treatment", "in-care", "complete"],
  adoption: ["explore", "complete"],
  surrogacy: ["explore", "complete"],
  "egg-donation": [
    "explore",
    "screening",
    "matched",
    "stimulation",
    "retrieval",
    "complete",
  ],
  "sperm-donation": [
    "explore",
    "screening",
    "approved",
    "donating",
    "complete",
  ],
};

/**
 * Get stages for a specific gender + goal combination
 *
 * Female preserve (egg freezing): no time-to-wait stage
 * Male preserve (sperm freezing): HAS time-to-wait stage (for analysis results)
 */
export function getStagesForJourney(
  gender: HomepageGender,
  goal: HomepageGoal,
): HomepageStage[] {
  // Donor journeys have their own stage sequences
  if (goal === "egg-donation" || goal === "sperm-donation") {
    return STAGES_BY_GOAL[goal];
  }

  // Adoption and surrogacy have simple two-stage sequences
  if (goal === "adoption" || goal === "surrogacy") {
    return STAGES_BY_GOAL[goal];
  }

  if (goal === "pregnancy") {
    return STAGES_BY_GOAL.pregnancy;
  }

  // Preserve / embryo-preserve flow - male has time-to-wait, female doesn't
  if (gender === "male") {
    return [
      "explore",
      "before-treatment",
      "in-care",
      "time-to-wait",
      "complete",
    ];
  }

  // Female preserve / embryo-preserve - no time-to-wait
  return STAGES_BY_GOAL[goal] ?? STAGES_BY_GOAL.preserve;
}

/**
 * Check if a stage is valid for a given goal
 * Note: For gender-aware validation, use isValidStageForJourney
 */
export function isValidStageForGoal(
  stage: HomepageStage,
  goal: HomepageGoal,
): boolean {
  return STAGES_BY_GOAL[goal].includes(stage);
}

/**
 * Check if a stage is valid for a given gender + goal combination
 */
export function isValidStageForJourney(
  stage: HomepageStage,
  gender: HomepageGender,
  goal: HomepageGoal,
): boolean {
  return getStagesForJourney(gender, goal).includes(stage);
}

/**
 * Get next stage for a given goal
 * Note: For gender-aware logic, use getNextStageForJourney
 */
export function getNextStage(
  currentStage: HomepageStage,
  goal: HomepageGoal,
): HomepageStage | null {
  const stages = STAGES_BY_GOAL[goal];
  const currentIndex = stages.indexOf(currentStage);
  if (currentIndex === -1 || currentIndex === stages.length - 1) {
    return null;
  }
  return stages[currentIndex + 1] ?? null;
}

/**
 * Get next stage for a given gender + goal combination
 */
export function getNextStageForJourney(
  currentStage: HomepageStage,
  gender: HomepageGender,
  goal: HomepageGoal,
): HomepageStage | null {
  const stages = getStagesForJourney(gender, goal);
  const currentIndex = stages.indexOf(currentStage);
  if (currentIndex === -1 || currentIndex === stages.length - 1) {
    return null;
  }
  return stages[currentIndex + 1] ?? null;
}

/**
 * Intent/Goal to HomepageGoal mapping
 *
 * Supports both legacy intent values (PascalCase) and new goal values (kebab-case).
 * New goal values map directly; legacy intent values are translated.
 */
export const INTENT_TO_GOAL: Record<string, HomepageGoal> = {
  // Legacy intent values
  Exploring: "pregnancy",
  Conception: "pregnancy",
  StartingTreatment: "pregnancy",
  InTreatment: "pregnancy",
  Preservation: "preserve",
  Donation: "egg-donation",
  // New goal values (identity mapping)
  exploring: "pregnancy", // No specific goal → default homepage
  preserve: "preserve",
  "egg-preserve": "preserve",
  "egg-freezing": "preserve",
  "sperm-preserve": "preserve",
  "sperm-freezing": "preserve",
  "embryo-preserve": "embryo-preserve",
  "embryo-freezing": "embryo-preserve",
  pregnancy: "pregnancy",
  "egg-donation": "egg-donation",
  "sperm-donation": "sperm-donation",
  adoption: "adoption",
  surrogacy: "surrogacy",
};

/**
 * Get the correct donation goal based on gender
 */
export function getGoalFromDonation(gender: HomepageGender): HomepageGoal {
  return gender === "male" ? "sperm-donation" : "egg-donation";
}

/**
 * Donor stage sequences
 */
export const DONOR_STAGES: Record<
  "egg-donation" | "sperm-donation",
  HomepageStage[]
> = {
  "egg-donation": [
    "explore",
    "screening",
    "matched",
    "stimulation",
    "retrieval",
    "complete",
  ],
  "sperm-donation": [
    "explore",
    "screening",
    "approved",
    "donating",
    "complete",
  ],
};

/**
 * Intent/Goal to starting Stage mapping
 *
 * Supports both legacy intent values and new goal values.
 * All new goals start at "explore" — stage progression is handled by homepageStage.
 */
export const INTENT_TO_STARTING_STAGE: Record<string, HomepageStage> = {
  // Legacy intent values
  Exploring: "explore",
  Conception: "explore",
  StartingTreatment: "before-treatment",
  Preservation: "before-treatment",
  Donation: "explore",
  InTreatment: "in-care",
  // New goal values — all start at explore
  exploring: "explore",
  preserve: "explore",
  "egg-freezing": "explore",
  "embryo-preserve": "explore",
  pregnancy: "explore",
  "egg-donation": "explore",
  "sperm-donation": "explore",
  adoption: "explore",
  surrogacy: "explore",
};

/**
 * Legacy state type for backwards compatibility
 */
export type LegacyHomepageState =
  | "female-explore"
  | "female-preserve"
  | "female-in-care"
  | "male-explore"
  | "male-in-care";
